import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import { format, calculateInvoiceTotals } from '../../helpers/invoiceNumber';
import moment from 'moment';
import '../../styles/page-common.css';
import '../../styles/list-glass.css';
import './dashboard-glass.css';

const initials = (nm) => (nm || '?').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const avColor = (nm) => { let h = 0; for (let c = 0; c < (nm || '').length; c++) h = (h * 31 + nm.charCodeAt(c)) % 6; return h; };

// Tax-inclusive total (matches the client running balance used for Outstanding,
// so Paid / Quotes / recent amounts are all real money on the same basis).
// calculateInvoiceTotals derives both tax lines from the line items themselves.
const invoiceTotal = (p) => calculateInvoiceTotals(p.descriptions, p.discount).total;

const clientName = (p) => p.client
  ? (p.client.name + ' ' + (p.client.surname || '')).trim()
  : (p.to || '—');

class Menu extends Component {
  fetchAll() {
    if (!this.props.selectedCompanyId) return;
    if (this.props.dispatchGetInvoices) this.props.dispatchGetInvoices();
    if (this.props.dispatchGetEntities) this.props.dispatchGetEntities();
    if (this.props.dispatchGetRecurring) this.props.dispatchGetRecurring();
    if (this.props.dispatchGetInvoiceTrend) this.props.dispatchGetInvoiceTrend();
  }

  componentDidMount() {
    this.fetchAll();
  }

  componentDidUpdate(prevProps) {
    if (this.props.selectedCompanyId && this.props.selectedCompanyId !== prevProps.selectedCompanyId) {
      this.fetchAll();
    }
  }

  render() {
    const { user, dashboard, company, invoices } = this.props;
    const profit = (dashboard && dashboard.Profit) || {};
    const firstName = (user && user.name) ? String(user.name).split(' ')[0] : '';
    const companyName = (company && company.companyName) || '';
    const fmt = (n) => format(Math.round(Number(n) || 0));

    // Currency + tax labels are company-configured: this app is multi-currency
    // and the tax isn't necessarily "VAT" (and there can be a second one), so
    // never hardcode either — read them from the company.
    const CUR_SYMBOLS = { ZAR: 'R', USD: '$', AUD: 'A$', NAD: 'N$', EUR: '€', GBP: '£' };
    const curCode = (company && company.currency) || 'ZAR';
    const cur = CUR_SYMBOLS[curCode] || curCode;             // symbol, else fall back to the code
    const multiCurrency = !!(company && company.multiCurrency);
    const taxCfg = (company && company.tax) || {};
    const taxNames = [];
    if (taxCfg.hasTax) {
      taxNames.push(taxCfg.tD || 'Tax');
      if (taxCfg.hasSecondTax) taxNames.push(taxCfg.t2D || 'Tax 2');
    }
    const taxLabel = taxNames.length ? taxNames.join(' + ') : 'tax'; // e.g. "VAT", "VAT + PST"

    const now = new Date();
    const hr = now.getHours();
    const tod = hr < 12 ? 'Good morning' : hr < 17 ? 'Good afternoon' : 'Good evening';
    let weekday = '', fullDate = '';
    try {
      weekday = now.toLocaleDateString('en-ZA', { weekday: 'long' });
      fullDate = now.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) {}
    const monthLabel = (() => { try { return now.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' }); } catch (e) { return ''; } })();

    // ---- Derive invoice metrics ----
    const list = (invoices || []).map((p) => ({
      id: p._id,
      name: clientName(p),
      number: p.invoiceNumber,
      isQuote: !!p.isQuote,
      paid: !!p.paid,
      sent: p.sent,
      dueRaw: p.dueDate,
      dateRaw: p.invoiceDate,
      total: invoiceTotal(p),
    }));
    const today = moment();
    const nonQuote = list.filter(i => !i.isQuote);
    const unpaid = nonQuote.filter(i => !i.paid);
    const quotes = list.filter(i => i.isQuote);
    const quoteTotal = quotes.reduce((s, i) => s + i.total, 0);

    const overdue = unpaid
      .filter(i => i.dueRaw && today.diff(moment(i.dueRaw), 'days') > 0)
      .sort((a, b) => today.diff(moment(a.dueRaw), 'days') < today.diff(moment(b.dueRaw), 'days') ? 1 : -1)
      .slice(0, 4);

    const recent = list.slice()
      .filter(i => i.dateRaw)
      .sort((a, b) => (a.dateRaw < b.dateRaw ? 1 : -1))
      .slice(0, 4);

    // Outstanding + who-owes from client (entity) running balances. These are
    // all-time balances (what the Clients page sums) — not period-scoped
    // invoices, so they reflect real money owed even outside the current month.
    const ents = (this.props.entities || []).map(e => ({
      id: e._id,
      name: (e.name + ' ' + (e.surname || '')).trim(),
      outstanding: parseFloat(e.outstanding) || 0,
    }));
    const totalOutstanding = ents.reduce((s, e) => s + e.outstanding, 0);
    const allOwing = ents.filter(e => e.outstanding > 0).sort((a, b) => b.outstanding - a.outstanding);
    const owingCount = allOwing.length;
    const owing = allOwing.slice(0, 5);

    const income = Number(profit.income) || 0;
    const expense = Number(profit.expense) || 0;
    const net = profit.total != null ? Number(profit.total) : (income - expense);
    const margin = income > 0 ? Math.round((net / income) * 100) : null;

    // ---- Monthly recurring revenue (MRR) ----
    // Sum active recurring invoices, normalising each schedule to a per-month
    // figure. Interval is (separation_count + 1) units of recurringType
    // (see calcNextRunDate), so monthly occurrences = (months per unit) / interval.
    const perMonthUnits = { day: 30.44, week: 4.3452, month: 1, year: 1 / 12 };
    const recurringInvoices = (this.props.recurring || []).filter(r => !!r.active);
    const recurringMonthly = recurringInvoices.reduce((acc, r) => {
      const { total, tax, tax2 } = calculateInvoiceTotals(r.descriptions, r.discount);
      const unit = (r.recurringType || 'month').replace(/s$/, '');
      const interval = (Number(r.separation_count) || 0) + 1;
      const occ = (perMonthUnits[unit] != null ? perMonthUnits[unit] : 1) / interval;
      acc.gross += total * occ;
      acc.tax += ((tax || 0) + (tax2 || 0)) * occ; // both tax lines combined
      return acc;
    }, { gross: 0, tax: 0 });
    const mrrTax = recurringMonthly.tax;             // monthly VAT/tax portion
    const mrrNet = recurringMonthly.gross - mrrTax;  // net recurring revenue (excl tax)
    const recurringCount = recurringInvoices.length;

    // ---- 6-month invoiced trend (server-aggregated; see invoices/trend) ----
    // Comes from the API, not the loaded invoices list — the dashboard only
    // holds the current view's invoices, so it can't bucket older months itself.
    const trendMonths = Array.isArray(this.props.invoiceTrend) ? this.props.invoiceTrend : [];
    // Scale bars across both this year and last year so the comparison is fair.
    const trendMax = Math.max(1, ...trendMonths.map(m => Math.max(m.total || 0, m.prevTotal || 0)));
    const lastIdx = trendMonths.length - 1;
    const curMonth = lastIdx >= 0 ? trendMonths[lastIdx] : null;
    const yoyThis = curMonth ? (curMonth.total || 0) : 0;        // current month, this year
    const yoyPrev = curMonth ? (curMonth.prevTotal || 0) : 0;    // current month, last year
    const yoyUp = yoyThis >= yoyPrev;
    const yoyPct = yoyPrev > 0 ? Math.round(((yoyThis - yoyPrev) / yoyPrev) * 100) : null;

    // Side boxes are conditional (expenses only if spent, quotes only if any),
    // so the grid distributes across however many actually render.
    const hasExpenses = expense > 0;
    const hasQuotes = quotes.length > 0;
    const sideCount = 2 + (hasExpenses ? 1 : 0) + (hasQuotes ? 1 : 0); // Invoices + Recurring (+Expenses)(+Quotes)

    return (
      <div
        className="content-wrapper accent-dashboard"
        style={{ ['--list-accent']: '#0DA1B2', ['--list-accent-dark']: '#067886' }}
      >
        <div className="dg-page" style={{ padding: '0 14px 24px 0' }}>

          {/* Hero */}
          <div className="gl-hero">
            <div className="gl-hero-row">
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.3px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.82)' }}>
                  {tod}{firstName ? `, ${firstName}` : ''}
                </div>
                <div className="gl-hero-title" style={{ marginTop: 4 }}>Here's your {weekday}</div>
                <div className="gl-hero-sub" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span>{fullDate}{companyName ? ` · ${companyName}` : ''}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#58E89C', boxShadow: '0 0 0 3px rgba(88,232,156,0.25)' }} />
                    All systems live
                  </span>
                </div>
              </div>
              <div className="gl-hero-actions">
                <Link to="/calendar" className="gl-hero-btn"><i className="far fa-calendar-plus" /> Book job</Link>
                <Link to="/expenses" className="gl-hero-btn"><i className="fas fa-receipt" /> Log expense</Link>
                <Link to="/invoices" className="gl-hero-btn solid"><i className="fas fa-plus" /> New invoice</Link>
              </div>
            </div>
          </div>

          {/* KPI grid */}
          <div className="dg-kpis">
            <div className="dg-kpi-main">
              <div className="dg-kpi-eyebrow">Net this month · {monthLabel}{multiCurrency ? ` · ${curCode}` : ''}</div>
              <div className="dg-kpi-amount-row">
                <div><span className="dg-kpi-cur">{cur} </span><span className="dg-kpi-num">{fmt(net)}</span></div>
                {margin != null && (
                  <span className="dg-kpi-delta"><i className="fas fa-percent" style={{ fontSize: 9 }} /> {margin}% margin</span>
                )}
              </div>
              <div className="dg-kpi-split">
                <div><div className="dg-kpi-split-lbl">Invoiced</div><div className="dg-kpi-split-val">{cur} {fmt(income)}</div></div>
                <div><div className="dg-kpi-split-lbl">Expenses</div><div className="dg-kpi-split-val">{cur} {fmt(expense)}</div></div>
                <div><div className="dg-kpi-split-lbl">Outstanding</div><div className="dg-kpi-split-val">{cur} {fmt(totalOutstanding)}</div></div>
              </div>
            </div>

            <div className="dg-kpi-sides" style={{ gridTemplateColumns: `repeat(${sideCount}, minmax(0,1fr))` }}>
              <Link to="/invoices" className="dg-kpi-side" style={{ textDecoration: 'none' }}>
                <div className="dg-ks-eyebrow">Invoiced · vs last yr</div>
                <div className="dg-trend-bars">
                  {trendMonths.map((m, k) => (
                    <div key={k} className="dg-trend-col" title={`${m.label} ${m.year}: ${cur} ${fmt(m.total)}  ·  ${m.prevYear}: ${cur} ${fmt(m.prevTotal)}`}>
                      <div className="dg-trend-pair">
                        <div className="dg-trend-bar cur" style={{ height: `${Math.max(3, ((m.total || 0) / trendMax) * 100)}%` }}></div>
                        <div className="dg-trend-bar prev" style={{ height: `${Math.max(3, ((m.prevTotal || 0) / trendMax) * 100)}%` }}></div>
                      </div>
                      <div className="dg-trend-lbl">{m.label}</div>
                    </div>
                  ))}
                  {trendMonths.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-muted, #8a99a8)', alignSelf: 'center' }}>Loading trend…</div>}
                </div>
                {trendMonths.length > 0 && (
                  <div className="dg-trend-legend"><span className="sw cur"></span>This yr<span className="sw prev"></span>Last yr</div>
                )}
                <div className="dg-ks-trend">
                  {cur} {fmt(yoyThis)} this mo
                  {yoyPct != null
                    ? <span className={`dg-ks-pill ${yoyUp ? 'up' : 'down'}`}>{yoyUp ? '▲' : '▼'} {Math.abs(yoyPct)}% YoY</span>
                    : <span className="dg-ks-pill up">vs last yr</span>}
                </div>
              </Link>

              <Link to="/recurring?type=invoices" className="dg-kpi-side" style={{ textDecoration: 'none' }}>
                <div className="dg-ks-eyebrow">Recurring / mo</div>
                <div className="dg-ks-amount-row"><span className="dg-ks-cur">{cur}</span><span className="dg-ks-amount">{fmt(mrrNet)}</span></div>
                <div className="dg-ks-note">excl. {taxLabel}{mrrTax > 0 ? ` · +${cur} ${fmt(mrrTax)}` : ''}</div>
                <div className="dg-ks-trend">
                  {recurringCount} active
                  <span className="dg-ks-pill up">Monthly</span>
                </div>
              </Link>

              {hasExpenses && (
                <Link to="/expenses" className="dg-kpi-side" style={{ textDecoration: 'none' }}>
                  <div className="dg-ks-eyebrow">Expenses</div>
                  <div className="dg-ks-amount-row"><span className="dg-ks-cur">{cur}</span><span className="dg-ks-amount">{fmt(expense)}</span></div>
                  <div className="dg-ks-note">incl. {taxLabel}</div>
                  <div className="dg-ks-trend">
                    This month
                    <span className="dg-ks-pill down">Spent</span>
                  </div>
                </Link>
              )}

              {hasQuotes && (
                <Link to="/invoices" className="dg-kpi-side" style={{ textDecoration: 'none' }}>
                  <div className="dg-ks-eyebrow">Quotes</div>
                  <div className="dg-ks-amount-row"><span className="dg-ks-cur">{cur}</span><span className="dg-ks-amount">{fmt(quoteTotal)}</span></div>
                  <div className="dg-ks-note">incl. {taxLabel}</div>
                  <div className="dg-ks-trend">
                    {quotes.length} open
                    <span className="dg-ks-pill flat">Pending</span>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Quick actions */}
          <div className="dg-mid">
            <div className="dg-shortcuts">
              <h4>Quick actions</h4>
              <div className="dg-sc-grid">
                <Link to="/invoices" className="dg-sc invoices"><div className="dg-sc-icon"><i className="fas fa-file-invoice-dollar" /></div><div className="dg-sc-meta"><div className="lbl">New invoice</div><div className="sub">Bill a client</div></div></Link>
                <Link to="/expenses" className="dg-sc expenses"><div className="dg-sc-icon"><i className="fas fa-receipt" /></div><div className="dg-sc-meta"><div className="lbl">Log expense</div><div className="sub">Snap a receipt</div></div></Link>
                <Link to="/calendar" className="dg-sc calendar"><div className="dg-sc-icon"><i className="far fa-calendar-plus" /></div><div className="dg-sc-meta"><div className="lbl">Book job</div><div className="sub">Schedule visit</div></div></Link>
                <Link to="/clients" className="dg-sc clients"><div className="dg-sc-icon"><i className="fas fa-user-plus" /></div><div className="dg-sc-meta"><div className="lbl">Add client</div><div className="sub">New contact</div></div></Link>
              </div>
            </div>
          </div>

          {/* Activity */}
          <div className="dg-activity">
            <div className="dg-panel">
              <div className="dg-panel-head">
                <div className="dg-panel-title-row">
                  <div className="dg-panel-title">Needs attention</div>
                  <span className="dg-panel-count">{owingCount}</span>
                </div>
                <Link className="dg-see-all" to="/clients">Resolve →</Link>
              </div>
              {owing.length === 0 && <div className="dg-empty">Nothing outstanding — nice work.</div>}
              {owing.map((c) => (
                <Link key={c.id} to="/clients" className="dg-ai attention">
                  <div className={`dg-ai-av dg-av-${avColor(c.name)}`}>{initials(c.name)}</div>
                  <div className="dg-ai-body">
                    <div className="dg-ai-title">{c.name}</div>
                    <div className="dg-ai-meta"><span>Outstanding balance</span></div>
                  </div>
                  <div className="dg-ai-right">
                    <div className="dg-ai-amt">{cur} {fmt(c.outstanding)}</div>
                    <span className="dg-pill overdue"><i className="fas fa-circle" /> Owing</span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="dg-panel">
              <div className="dg-panel-head">
                <div className="dg-panel-title-row">
                  <div className="dg-panel-title">Recent activity</div>
                  <span className="dg-panel-count">{recent.length}</span>
                </div>
                <Link className="dg-see-all" to="/invoices">View all →</Link>
              </div>
              {recent.length === 0 && <div className="dg-empty">No invoices yet.</div>}
              {recent.map((i) => {
                const cls = i.isQuote ? 'invoiced' : i.paid ? 'paid' : 'invoiced';
                const label = i.isQuote ? 'Quote' : i.paid ? 'Paid' : (i.sent ? 'Sent' : 'Created');
                return (
                  <Link key={i.id} to="/invoices" className={`dg-ai ${cls}`}>
                    <div className={`dg-ai-av dg-av-${avColor(i.name)}`}>{initials(i.name)}</div>
                    <div className="dg-ai-body">
                      <div className="dg-ai-title">{label} · {i.name}</div>
                      <div className="dg-ai-meta"><span>#{i.number}</span><span className="dot" /><span>{i.dateRaw ? moment(i.dateRaw).format('DD MMM') : ''}</span></div>
                    </div>
                    <div className="dg-ai-right">
                      <div className={`dg-ai-amt ${i.paid && !i.isQuote ? 'paid' : ''}`}>{i.paid && !i.isQuote ? '+' : ''}{cur} {fmt(i.total)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Onboarding (self-hides once complete) */}
          <OnboardingChecklist />
        </div>
      </div>
    );
  }
}

export { Menu };
